import {useEffect, useMemo, useRef, useCallback, useSyncExternalStore, type RefObject} from 'react'
import {Canvas, useThree, useFrame} from '@react-three/fiber'
import {OrbitControls, Environment} from '@react-three/drei'
import * as THREE from 'three'
import type {OrbitControls as OrbitControlsImpl} from 'three-stdlib'
import {RectAreaLightUniformsLib} from 'three/addons/lights/RectAreaLightUniformsLib.js'
import DeviceMeshV2 from './deviceMesh/DeviceMeshV2'
import Text3DMeshV2 from './deviceMesh/Text3DMeshV2'
import Sprite3DMeshV2 from './deviceMesh/Sprite3DMeshV2'
import CustomDecalMeshV2 from './deviceMesh/CustomDecalMeshV2'
import ExportHandler from './ExportHandler'
import type {RenderExportFn} from './ExportHandler'
import {useAppSelector, useAppDispatch} from '../store'
import ShadowCatcher from './deviceMesh/ShadowCatcher'
import {SCENE_MAX_REACH, CAMERA_FOV_DEG, MAX_SHADOW_REACH} from '../constants/scene3d'
import {DREI_HDRI_CDN, DREI_PRESETS} from '../constants/envPresets'
import {
  selectActiveScene,
  selectAllScenes,
  selectActiveSceneIndex,
  setCameraPosition,
  setCameraPositionForScene,
} from './store/scenesSlice'
import type {GradientStop} from './store/scenesSlice'
import {
  getSnapshot as getCameraPresetSnapshot,
  subscribe as subscribeCameraPreset,
} from './deviceMesh/cameraPresetStore'
import {registerBgRedraw, type RedrawVirtualContext} from './sceneBackgroundStore'
import {setViewportAspect} from './viewportSizeStore'
import {carouselGapPx} from '../constants/exportPresets'
import {addBreadcrumb} from '../errors/breadcrumb'
import {reportError} from '../errors/reportError'
import {loadSceneImage} from '../utils/sceneImageLoader'

// Camera defaults — hardcoded, not stored in config. `CAMERA_FOV_DEG` is
// shared via `scene3d.ts` so PositionPad3D can size its X/Y range against
// the same frustum the viewport renders.
const CAMERA_FOV = CAMERA_FOV_DEG
const CAMERA_NEAR = 1
const CAMERA_FAR = 2000

// Orbit constraints — zoom distance limits and pan fraction
const ORBIT_MIN_DISTANCE = 50
// Generous enough that a fit-scaled camera preset on a large device + narrow export
// aspect (max custom device reaches ≈ 1100 units out) isn't clamped back in by
// OrbitControls.update(). Stays within CAMERA_FAR (2000).
const ORBIT_MAX_DISTANCE = 1500
// const PAN_FRUSTUM_FRACTION = 0.8 // Used by PanLimiter (commented out)

// Free camera mode — removes all orbit constraints (rotation, zoom, pan)
const FREE_CAMERA = import.meta.env.VITE_FREE_CAMERA === 'true'

// Shadow defaults — these are only the INITIAL declarative values. Every frame
// `ShadowFrustumFitter` (inside `SceneLighting`) re-fits each directional
// light's orthographic shadow camera to the actually-visible scene region so
// shadows never get clipped on wide/tall exports (e.g. feature graphics), while
// keeping the frustum as tight as possible for the common case to preserve
// per-texel shadow resolution at the fixed `SHADOW_MAP_SIZE`.
//
// `SHADOW_CAMERA_BOUNDS` doubles as the fitter's MINIMUM reach (the floor it
// never shrinks below), sized off `SCENE_MAX_REACH` so an object placed
// anywhere inside the editor's allowed 3D volume still casts a visible shadow.
//
// Map size 2048 is capped (iOS / SwiftShader); the fitter trades texel density
// for coverage only as far as the visible region demands.
const SHADOW_MAP_SIZE = 2048
const SHADOW_CAMERA_BOUNDS = SCENE_MAX_REACH + 100
const SHADOW_CAMERA_NEAR = 0.5
const SHADOW_CAMERA_FAR = 1500
const SHADOW_BIAS = -0.0005
const SHADOW_NORMAL_BIAS = 0.02

// Half-extent of the caster volume the fitted shadow frustum must always
// contain (device + 3D objects live within ±SCENE_MAX_REACH of origin on every
// axis). The visible catcher region (sized via the camera) is unioned with it.
const SHADOW_CASTER_HALF = SCENE_MAX_REACH
// Small padding (world units) added around the fitted frustum bounds.
const SHADOW_FIT_MARGIN = 5

// Tone mapping — hardcoded constant
const TONE_MAPPING_EXPOSURE = 1.0

// ── Percentage → Three.js intensity (100% = 5.0) ─────────────────

const MAX_LIGHT_INTENSITY = 5.0
const MAX_AMBIENT_INTENSITY = 2.0
const MAX_ENV_INTENSITY = 3.0

const pctToIntensity = (pct: number, max: number) => (pct / 100) * max

// ── SceneBackground — reads background state from active scene ────

const SceneBackground = () => {
  const {scene, gl} = useThree()
  const bgMode = useAppSelector((s) => selectActiveScene(s).bgMode)
  const bgGradientStops = useAppSelector((s) => selectActiveScene(s).bgGradientStops)
  const bgAngle = useAppSelector((s) => selectActiveScene(s).bgAngle)
  const bgImageUrl = useAppSelector((s) => selectActiveScene(s).bgImageUrl)
  const bgImageFit = useAppSelector((s) => selectActiveScene(s).bgImageFit)
  const bgImageOffsetX = useAppSelector((s) => selectActiveScene(s).bgImageOffsetX)
  const bgImageOffsetY = useAppSelector((s) => selectActiveScene(s).bgImageOffsetY)
  const bgImageZoom = useAppSelector((s) => selectActiveScene(s).bgImageZoom)
  const exportWidth = useAppSelector((s) => selectActiveScene(s).exportWidth)
  const exportHeight = useAppSelector((s) => selectActiveScene(s).exportHeight)
  const carouselEnabled = useAppSelector((s) => selectActiveScene(s).exportCarouselEnabled)
  const carouselCount = useAppSelector((s) => selectActiveScene(s).exportCarouselCount)
  const carouselGap = useAppSelector((s) => selectActiveScene(s).exportCarouselGap)
  // BG layout reference must match what per-slice exports use: the FULL carousel
  // strip (exportWidth × N + gutters), not a single slice. Otherwise `cover` flips
  // its anchor dimension between viewport and render and the user-set zoom % ends
  // up applied to two different base dimensions.
  const bgSlices = carouselEnabled ? carouselCount : 1
  const bgFrameWidth = exportWidth * bgSlices + carouselGapPx(exportWidth, carouselGap) * (bgSlices - 1)

  // Cache the loaded image so redraws (e.g. for export) don't re-fetch.
  const imageRef = useRef<{url: string; image: HTMLImageElement} | null>(null)
  // Latest texture so we can dispose the previous one across redraws.
  const textureRef = useRef<THREE.Texture | null>(null)
  // Latest bg state in a ref so the imperative redraw callback always sees fresh values.
  const stateRef = useRef({
    bgMode,
    bgImageFit,
    bgImageOffsetX,
    bgImageOffsetY,
    bgImageZoom,
    bgGradientStops,
    bgAngle,
    exportWidth: bgFrameWidth,
    exportHeight,
  })
  stateRef.current = {
    bgMode,
    bgImageFit,
    bgImageOffsetX,
    bgImageOffsetY,
    bgImageZoom,
    bgGradientStops,
    bgAngle,
    exportWidth: bgFrameWidth,
    exportHeight,
  }

  /* eslint-disable react-hooks/immutability -- standard Three.js scene setup */

  // Compute the export-aspect rectangle inside a canvas of size (cw × ch).
  // Letterboxes when canvas aspect ≠ export aspect so the editor preview shows
  // the same crop the export will produce.
  const computeFrameRect = (cw: number, ch: number, exportW: number, exportH: number) => {
    const exportAspect = exportW / exportH
    const canvasAspect = cw / ch
    let frameW: number, frameH: number
    if (canvasAspect > exportAspect) {
      frameH = ch
      frameW = ch * exportAspect
    } else {
      frameW = cw
      frameH = cw / exportAspect
    }
    const frameX = (cw - frameW) / 2
    const frameY = (ch - frameH) / 2
    return {frameX, frameY, frameW, frameH}
  }

  const drawImageBgTexture = useCallback(
    (cw: number, ch: number, img: HTMLImageElement, virtual?: RedrawVirtualContext): THREE.CanvasTexture => {
      const {
        bgImageFit: fit,
        bgImageOffsetX: ox100,
        bgImageOffsetY: oy100,
        bgImageZoom: zoom,
        exportWidth: ew,
        exportHeight: eh,
      } = stateRef.current
      const canvas = document.createElement('canvas')
      canvas.width = cw
      canvas.height = ch
      const ctx = canvas.getContext('2d')!
      // When `virtual` is set the frame fills the (fullW × fullH) virtual canvas
      // and the slice canvas (cw × ch) shows only the sub-rect at (offsetX,
      // offsetY) — used by ExportHandler for carousel slices.
      const {frameX, frameY, frameW, frameH} = virtual
        ? {frameX: -virtual.offsetX, frameY: -virtual.offsetY, frameW: virtual.fullWidth, frameH: virtual.fullHeight}
        : computeFrameRect(cw, ch, ew, eh)
      const iw = img.naturalWidth
      const ih = img.naturalHeight
      const imgAspect = iw / ih
      const frameAspect = frameW / frameH
      let dw: number, dh: number
      if (fit === 'fill') {
        dw = frameW
        dh = frameH
      } else if (fit === 'cover') {
        if (imgAspect > frameAspect) {
          dh = frameH
          dw = frameH * imgAspect
        } else {
          dw = frameW
          dh = frameW / imgAspect
        }
      } else {
        if (imgAspect > frameAspect) {
          dw = frameW
          dh = frameW / imgAspect
        } else {
          dh = frameH
          dw = frameH * imgAspect
        }
      }
      dw *= zoom
      dh *= zoom
      const centerX = frameX + (frameW - dw) / 2
      const centerY = frameY + (frameH - dh) / 2
      const maxTravelX = Math.max(0, (dw - frameW) / 2)
      const maxTravelY = Math.max(0, (dh - frameH) / 2)
      const ox = centerX - (ox100 / 100) * maxTravelX
      const oy = centerY - (oy100 / 100) * maxTravelY
      ctx.fillStyle = '#000000'
      ctx.fillRect(0, 0, cw, ch)
      // Draw without clipping — the image scale is locked to the export-aspect
      // frame, so inside the frame matches the export, while areas outside the
      // frame fill naturally with the rest of the source image (cover mode) or
      // stay black (contain/fill). For carousel slices, drawing coords already
      // include the -offsetX shift via resolveFrameRect.
      ctx.drawImage(img, ox, oy, dw, dh)
      const tex = new THREE.CanvasTexture(canvas)
      tex.colorSpace = THREE.SRGBColorSpace
      return tex
    },
    [],
  )

  const drawGradientBgTexture = useCallback(
    (cw: number, ch: number, virtual?: RedrawVirtualContext): THREE.CanvasTexture => {
      const {bgGradientStops: stops, bgAngle: angle, exportWidth: ew, exportHeight: eh} = stateRef.current
      const canvas = document.createElement('canvas')
      canvas.width = cw
      canvas.height = ch
      const ctx = canvas.getContext('2d')!
      const {frameX, frameY, frameW, frameH} = virtual
        ? {frameX: -virtual.offsetX, frameY: -virtual.offsetY, frameW: virtual.fullWidth, frameH: virtual.fullHeight}
        : computeFrameRect(cw, ch, ew, eh)
      const rad = (angle * Math.PI) / 180
      // Gradient line spans the frame's bounding diagonal so all stops are visible
      // regardless of frame aspect or angle.
      const half = Math.sqrt(frameW * frameW + frameH * frameH) / 2
      const cx = frameX + frameW / 2
      const cy = frameY + frameH / 2
      const x1 = cx - Math.cos(rad) * half
      const y1 = cy - Math.sin(rad) * half
      const x2 = cx + Math.cos(rad) * half
      const y2 = cy + Math.sin(rad) * half
      const gradient = ctx.createLinearGradient(x1, y1, x2, y2)
      const sorted = [...stops].sort((a: GradientStop, b: GradientStop) => a.position - b.position)
      sorted.forEach((stop: GradientStop) => {
        gradient.addColorStop(stop.position, stop.color)
      })
      // Fill entire canvas — the axis is anchored to the frame, so inside the
      // frame the gradient matches the export; outside the frame the gradient
      // continues (clamped to the nearest end-stop color) instead of black bars.
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, cw, ch)
      const tex = new THREE.CanvasTexture(canvas)
      tex.colorSpace = THREE.SRGBColorSpace
      return tex
    },
    [],
  )

  const setBackgroundTexture = useCallback(
    (tex: THREE.Texture) => {
      textureRef.current?.dispose()
      textureRef.current = tex
      scene.background = tex
    },
    [scene],
  )

  useEffect(() => {
    if (bgMode === 'gradient') {
      const vpSize = gl.getSize(new THREE.Vector2())
      const cw = vpSize.x || 512
      const ch = vpSize.y || 512
      setBackgroundTexture(drawGradientBgTexture(cw, ch))
    } else if (bgMode === 'image' && bgImageUrl) {
      const cached = imageRef.current
      const drawNow = (img: HTMLImageElement) => {
        const vpSize = gl.getSize(new THREE.Vector2())
        const cw = vpSize.x || 1024
        const ch = vpSize.y || 1024
        setBackgroundTexture(drawImageBgTexture(cw, ch, img))
      }
      if (cached && cached.url === bgImageUrl) {
        drawNow(cached.image)
      } else {
        return loadSceneImage(
          bgImageUrl,
          (img) => {
            imageRef.current = {url: bgImageUrl, image: img}
            drawNow(img)
          },
          (failure) => {
            // Background download failed — keep a plain white background rather
            // than leaving the scene in a half-loaded state.
            reportError(new Error('Background image failed to load'), 'background.loadError', {
              url: bgImageUrl,
              ...failure,
            })
            textureRef.current?.dispose()
            textureRef.current = null
            scene.background = new THREE.Color('#ffffff')
          },
        )
      }
    } else {
      textureRef.current?.dispose()
      textureRef.current = null
      scene.background = new THREE.Color('#ffffff')
    }
  }, [
    scene,
    gl,
    bgMode,
    bgGradientStops,
    bgAngle,
    bgImageUrl,
    bgImageFit,
    bgImageOffsetX,
    bgImageOffsetY,
    bgImageZoom,
    bgFrameWidth,
    exportHeight,
    drawImageBgTexture,
    drawGradientBgTexture,
    setBackgroundTexture,
  ])

  // Imperative redraw — used by ExportHandler to rebuild the background at the
  // export aspect right before rendering, then restore it for the editor. The
  // optional `virtual` arg carries the carousel-slice extraction context.
  useEffect(() => {
    registerBgRedraw((width, height, virtual) => {
      const cw = Math.max(1, Math.round(width))
      const ch = Math.max(1, Math.round(height))
      const mode = stateRef.current.bgMode
      if (mode === 'gradient') {
        setBackgroundTexture(drawGradientBgTexture(cw, ch, virtual))
      } else if (mode === 'image') {
        const cached = imageRef.current
        if (cached) setBackgroundTexture(drawImageBgTexture(cw, ch, cached.image, virtual))
      }
    })
    return () => registerBgRedraw(null)
  }, [drawImageBgTexture, drawGradientBgTexture, setBackgroundTexture])

  return null
}

// ── RectAreaLight initialisation guard ────────────────────────────

let rectAreaInited = false
const ensureRectAreaInit = () => {
  if (!rectAreaInited) {
    RectAreaLightUniformsLib.init()
    rectAreaInited = true
  }
}

// ── RectAreaLightHelper — makes the light look at the origin ──────

const RectAreaLightMesh = ({
  color,
  intensity,
  position,
  width,
  height,
}: {
  color: string
  intensity: number
  position: [number, number, number]
  width: number
  height: number
}) => {
  const ref = useRef<THREE.RectAreaLight>(null)

  useEffect(() => {
    if (ref.current) {
      ref.current.lookAt(0, 0, 0)
    }
  }, [position])

  return (
    <rectAreaLight ref={ref} color={color} intensity={intensity} position={position} width={width} height={height} />
  )
}

// ── ShadowFrustumFitter — per-frame fit of each light's shadow camera ──
//
// The orthographic shadow camera of every directional light is re-fit each
// frame to the union of (a) the caster volume (±SHADOW_CASTER_HALF on all axes)
// and (b) the actually-visible region of the catcher plane (a centered square
// whose half-extent is derived from the live camera frustum ∩ catcher plane).
// This keeps the frustum tight (good texel density) for the common portrait/
// square case but expands it for wide/tall exports (feature graphics) so the
// shadow never clips before the edge of the composition.
//
// Runs at default useFrame priority (0) — before `ExportAspectRender`'s
// priority-1 manual render — reading the camera's frustum from the previous
// frame's `setViewOffset` (one-frame lag, self-correcting under frameloop=always).

// Reused temporaries (avoid per-frame allocation).
const _fitCamPos = new THREE.Vector3()
const _fitPoint = new THREE.Vector3()
const _fitDir = new THREE.Vector3()
const _fitLightPos = new THREE.Vector3()
const _fitVec = new THREE.Vector3()
const _fitWorld = new THREE.Matrix4()
const _fitView = new THREE.Matrix4()
const _FIT_UP = new THREE.Vector3(0, 1, 0)
const _FIT_ORIGIN = new THREE.Vector3(0, 0, 0)

const NDC_CORNERS: ReadonlyArray<readonly [number, number]> = [
  [-1, -1],
  [1, -1],
  [1, 1],
  [-1, 1],
]
const BOX_SIGNS: ReadonlyArray<readonly [number, number, number]> = [
  [-1, -1, -1],
  [1, -1, -1],
  [-1, 1, -1],
  [1, 1, -1],
  [-1, -1, 1],
  [1, -1, 1],
  [-1, 1, 1],
  [1, 1, 1],
]

// Half-extent (centered on origin) of the visible catcher-plane region: shoots
// the 4 camera-frustum corner rays at the catcher plane and takes the largest
// |x|/|y| hit, clamped to [SHADOW_CAMERA_BOUNDS, MAX_SHADOW_REACH]. Falls back to
// MAX when a corner ray is parallel to / behind the plane (grazing camera).
const computeVisibleReach = (camera: THREE.Camera, catcherZ: number): number => {
  camera.getWorldPosition(_fitCamPos)
  let reach = SHADOW_CAMERA_BOUNDS
  for (let i = 0; i < NDC_CORNERS.length; i++) {
    const [nx, ny] = NDC_CORNERS[i]
    _fitPoint.set(nx, ny, 0.5).unproject(camera)
    _fitDir.copy(_fitPoint).sub(_fitCamPos)
    if (Math.abs(_fitDir.z) < 1e-4) return MAX_SHADOW_REACH
    const t = (catcherZ - _fitCamPos.z) / _fitDir.z
    if (t <= 0) return MAX_SHADOW_REACH
    const x = _fitCamPos.x + _fitDir.x * t
    const y = _fitCamPos.y + _fitDir.y * t
    reach = Math.max(reach, Math.abs(x), Math.abs(y))
  }
  return Math.min(reach, MAX_SHADOW_REACH)
}

// Fit one directional light's orthographic shadow camera to the bounding box,
// in light-view space, of the caster cube + the visible catcher square.
const fitLightShadow = (light: THREE.DirectionalLight, coverHalf: number, catcherZ: number) => {
  const cam = light.shadow.camera as THREE.OrthographicCamera
  // Light-view matrix: eye = light world position, aims at origin, up = +Y —
  // matches how three's DirectionalLightShadow orients its shadow camera.
  light.getWorldPosition(_fitLightPos)
  _fitWorld.lookAt(_fitLightPos, _FIT_ORIGIN, _FIT_UP)
  _fitWorld.setPosition(_fitLightPos)
  _fitView.copy(_fitWorld).invert()

  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  let minDist = Infinity
  let maxDist = -Infinity

  const accumulate = (wx: number, wy: number, wz: number) => {
    _fitVec.set(wx, wy, wz).applyMatrix4(_fitView)
    if (_fitVec.x < minX) minX = _fitVec.x
    if (_fitVec.x > maxX) maxX = _fitVec.x
    if (_fitVec.y < minY) minY = _fitVec.y
    if (_fitVec.y > maxY) maxY = _fitVec.y
    const dist = -_fitVec.z // view-space looks down -Z; distance in front is positive
    if (dist < minDist) minDist = dist
    if (dist > maxDist) maxDist = dist
  }

  for (let i = 0; i < BOX_SIGNS.length; i++) {
    const s = BOX_SIGNS[i]
    accumulate(s[0] * SHADOW_CASTER_HALF, s[1] * SHADOW_CASTER_HALF, s[2] * SHADOW_CASTER_HALF)
  }
  for (let i = 0; i < NDC_CORNERS.length; i++) {
    const s = NDC_CORNERS[i]
    accumulate(s[0] * coverHalf, s[1] * coverHalf, catcherZ)
  }

  cam.left = minX - SHADOW_FIT_MARGIN
  cam.right = maxX + SHADOW_FIT_MARGIN
  cam.bottom = minY - SHADOW_FIT_MARGIN
  cam.top = maxY + SHADOW_FIT_MARGIN
  cam.near = Math.max(0.1, minDist - SHADOW_FIT_MARGIN)
  cam.far = maxDist + SHADOW_FIT_MARGIN
  cam.updateProjectionMatrix()
}

const ShadowFrustumFitter = ({lightsRef}: {lightsRef: RefObject<Map<string, THREE.DirectionalLight>>}) => {
  const deviceDepth = useAppSelector((s) => selectActiveScene(s).deviceDepth)
  const groundDistance = useAppSelector((s) => selectActiveScene(s).groundDistance)
  const catcherZ = -(deviceDepth / 2) - groundDistance

  useFrame((state) => {
    const map = lightsRef.current
    if (!map || map.size === 0) return
    const coverHalf = computeVisibleReach(state.camera, catcherZ)
    map.forEach((light) => {
      if (light.castShadow) fitLightShadow(light, coverHalf, catcherZ)
    })
  })

  return null
}

// ── SceneLighting — reads lighting state from active scene ────────

const SceneLighting = () => {
  const lights = useAppSelector((s) => selectActiveScene(s).lights)
  const ambientIntensity = useAppSelector((s) => selectActiveScene(s).ambientIntensity)
  const ambientColor = useAppSelector((s) => selectActiveScene(s).ambientColor)

  // Live directional-light instances, keyed by id, for the per-frame fitter.
  const lightsRef = useRef<Map<string, THREE.DirectionalLight>>(new Map())

  ensureRectAreaInit()

  return (
    <>
      <ShadowFrustumFitter lightsRef={lightsRef} />
      <ambientLight intensity={pctToIntensity(ambientIntensity, MAX_AMBIENT_INTENSITY)} color={ambientColor} />
      {lights.map((light) => {
        if (light.type === 'rectarea') {
          return (
            <RectAreaLightMesh
              key={light.id}
              color={light.color}
              intensity={light.intensity}
              position={[light.position[0], light.position[1], light.position[2]]}
              width={light.width ?? 50}
              height={light.height ?? 50}
            />
          )
        }

        // Default: directionalLight
        const castShadow = light.shadowIntensity > 0
        const shadowIntensity = light.shadowIntensity / 100
        const shadowRadius = (light.shadowSoftness / 100) * 20
        return (
          <directionalLight
            key={light.id}
            ref={(el) => {
              if (el) lightsRef.current.set(light.id, el)
              else lightsRef.current.delete(light.id)
            }}
            position={[light.position[0], light.position[1], light.position[2]]}
            intensity={pctToIntensity(light.intensity, MAX_LIGHT_INTENSITY)}
            color={light.color}
            castShadow={castShadow}
            shadow-mapSize-width={SHADOW_MAP_SIZE}
            shadow-mapSize-height={SHADOW_MAP_SIZE}
            shadow-camera-left={-SHADOW_CAMERA_BOUNDS}
            shadow-camera-right={SHADOW_CAMERA_BOUNDS}
            shadow-camera-top={SHADOW_CAMERA_BOUNDS}
            shadow-camera-bottom={-SHADOW_CAMERA_BOUNDS}
            shadow-camera-near={SHADOW_CAMERA_NEAR}
            shadow-camera-far={SHADOW_CAMERA_FAR}
            shadow-bias={SHADOW_BIAS}
            shadow-normalBias={SHADOW_NORMAL_BIAS}
            shadow-radius={shadowRadius}
            shadow-blurSamples={16}
            shadow-intensity={shadowIntensity}
          />
        )
      })}
    </>
  )
}

// ── NoSpecularFromLights — remove direct-light specular, keep env map reflections

const patchNoDirectSpecular = (shader: {fragmentShader: string}) => {
  // Remove directSpecular from the totalSpecular sum
  shader.fragmentShader = shader.fragmentShader.replace(
    'vec3 totalSpecular = reflectedLight.directSpecular + reflectedLight.indirectSpecular;',
    'vec3 totalSpecular = reflectedLight.indirectSpecular;',
  )
  // Remove direct clearcoat specular if clearcoat is used
  shader.fragmentShader = shader.fragmentShader.replace(
    'clearcoatSpecularDirect + clearcoatSpecularIndirect',
    'clearcoatSpecularIndirect',
  )
  // Remove direct sheen specular if sheen is used
  shader.fragmentShader = shader.fragmentShader.replace(
    'sheenSpecularDirect + sheenSpecularIndirect',
    'sheenSpecularIndirect',
  )
}

const NoSpecularFromLights = () => {
  const {scene} = useThree()

  const patchMaterials = useCallback(() => {
    scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material]
      mats.forEach((mat) => {
        if (!(mat instanceof THREE.MeshPhysicalMaterial)) return
        if ((mat.userData as Record<string, boolean>).__noDirectSpec) return
        mat.onBeforeCompile = patchNoDirectSpecular
        ;(mat.userData as Record<string, boolean>).__noDirectSpec = true
        mat.needsUpdate = true
      })
    })
  }, [scene])

  // Re-patch whenever scene children change
  useEffect(() => {
    patchMaterials()
    const id = setInterval(patchMaterials, 1000)
    return () => clearInterval(id)
  }, [patchMaterials])

  return null
}

// ── SceneEnvironment — reads env map state from active scene ──────

const SceneEnvironment = () => {
  const envPreset = useAppSelector((s) => selectActiveScene(s).envPreset)
  const envMapIntensityPct = useAppSelector((s) => selectActiveScene(s).envMapIntensity)
  const intensity = pctToIntensity(envMapIntensityPct, MAX_ENV_INTENSITY)

  // Pass files/path explicitly (self-hosted fe/public/hdri/) instead of `preset` — drei's
  // `preset` prop always resolves through its own hardcoded raw.githack.com CDN internally,
  // bypassing any override (see docs/editor.md).
  const file = DREI_PRESETS[envPreset]
  if (!file) return null
  return <Environment files={file} path={DREI_HDRI_CDN} environmentIntensity={intensity} />
}

// ── ExportAspectRender — render the 3D scene WYSIWYG with the export aspect
//
// Uses `cam.setViewOffset(sub_w, sub_h, -sub_x, -sub_y, canvas_w, canvas_h)` to
// EXPAND the camera's frustum so the export-aspect projection sits in the
// middle of the canvas and the rest of the canvas shows extra scene around it.
// Internally Three.js computes:
//   width  *= canvas_w / sub_w   (frustum gets wider than the export camera's)
//   height *= canvas_h / sub_h   (frustum gets taller than the export camera's)
//   left   += -sub_x * width_orig / sub_w  (negative offset shifts left)
// — which centers the original export-camera frustum inside an enlarged frustum
// that covers the full canvas. Inside the sub-region (= export frame overlay)
// the projection matches the export camera byte-for-byte; outside, the user
// sees more of the scene around the device (so they can compose without the
// frame clipping objects out of view).
//
// One pass, no scissor — simpler than the previous two-pass approach and lets
// objects naturally extend past the export frame in both viewport and renders'
// visible composition. SceneBackground is unchanged (canvas-sized with the
// export-aspect frame letterboxed inside via `computeFrameRect`), so the bg's
// outer extent doesn't change when the user picks a different resolution.

const ExportAspectRender = () => {
  const exportWidth = useAppSelector((s) => selectActiveScene(s).exportWidth)
  const exportHeight = useAppSelector((s) => selectActiveScene(s).exportHeight)
  const carouselEnabled = useAppSelector((s) => selectActiveScene(s).exportCarouselEnabled)
  const carouselCount = useAppSelector((s) => selectActiveScene(s).exportCarouselCount)
  const carouselGap = useAppSelector((s) => selectActiveScene(s).exportCarouselGap)
  const slices = carouselEnabled ? carouselCount : 1
  // Include the inter-slice gutter so the editor framing matches the gapped strip
  // the per-slice exports are cut from.
  const exportAspect = (exportWidth * slices + carouselGapPx(exportWidth, carouselGap) * (slices - 1)) / exportHeight

  const {size} = useThree()

  // Publish canvas aspect to the shared store — PositionPad3D mirrors it so
  // its NDC mapping matches what's actually drawn on screen.
  useEffect(() => {
    if (size.width > 0 && size.height > 0) setViewportAspect(size.width / size.height)
  }, [size.width, size.height])

  // Contain the export aspect inside the canvas (mirrors ExportFrameOverlay).
  const subRegion = useMemo(() => {
    const vpW = size.width
    const vpH = size.height
    if (vpW === 0 || vpH === 0) return {x: 0, y: 0, w: 0, h: 0}
    const widthIfHeightFits = vpH * exportAspect
    if (widthIfHeightFits <= vpW) {
      const w = widthIfHeightFits
      return {x: (vpW - w) / 2, y: 0, w, h: vpH}
    }
    const h = vpW / exportAspect
    return {x: 0, y: (vpH - h) / 2, w: vpW, h}
  }, [size.width, size.height, exportAspect])

  useFrame((state) => {
    if (subRegion.w === 0 || subRegion.h === 0) return
    const cam = state.camera as THREE.PerspectiveCamera
    const vpW = state.size.width
    const vpH = state.size.height
    if (Math.abs(cam.aspect - exportAspect) > 1e-4) {
      cam.aspect = exportAspect
    }
    // Expand the frustum across the full canvas; export-aspect rect lands at
    // (subRegion.x, subRegion.y, subRegion.w, subRegion.h).
    cam.setViewOffset(subRegion.w, subRegion.h, -subRegion.x, -subRegion.y, vpW, vpH)
    cam.updateProjectionMatrix()

    state.gl.setScissorTest(false)
    state.gl.setViewport(0, 0, vpW, vpH)
    state.gl.autoClearColor = true
    state.gl.autoClearDepth = true
    state.gl.render(state.scene, cam)
  }, 1)

  return null
}

// ── CameraSync — keeps Redux camera in sync with Three.js camera
//
// Camera actions are transparent (bypass undo stack), so dispatching every
// frame is safe — no undo pollution, Redux always has the live position.
// On scene switch: save to old scene, restore from new scene.

const CameraSync = ({controlsRef}: {controlsRef: RefObject<OrbitControlsImpl | null>}) => {
  const dispatch = useAppDispatch()
  const {camera} = useThree()
  const activeIndex = useAppSelector(selectActiveSceneIndex)
  const allScenes = useAppSelector(selectAllScenes)
  const prevIndexRef = useRef(activeIndex)
  // Track activeIndex in a ref so useFrame can skip dispatches during scene switches
  const activeIndexRef = useRef(activeIndex)
  useEffect(() => {
    activeIndexRef.current = activeIndex
  }, [activeIndex])

  // Dispatch live camera position + rotation to Redux at most every 500ms (transparent — no undo entry).
  // Uses time-based throttle that DROPS intermediate updates rather than queuing them.
  // MOVE_THRESHOLD filters out micro-movements from OrbitControls damping/easing.
  const lastPosRef = useRef<[number, number, number, number, number, number] | null>(null)
  const lastDispatchTimeRef = useRef(0)
  const THROTTLE_MS = 500
  const MOVE_THRESHOLD = 0.01
  useFrame(() => {
    // Skip dispatch during scene switch — camera hasn't been restored yet
    if (activeIndexRef.current !== prevIndexRef.current) return
    const now = performance.now()
    if (now - lastDispatchTimeRef.current < THROTTLE_MS) return
    const px = camera.position.x
    const py = camera.position.y
    const pz = camera.position.z
    const rx = camera.rotation.x
    const ry = camera.rotation.y
    const rz = camera.rotation.z

    // Skip dispatch if movement is below threshold (filters OrbitControls damping noise)
    if (lastPosRef.current) {
      const [lx, ly, lz, lrx, lry, lrz] = lastPosRef.current
      const posDist = Math.sqrt((px - lx) ** 2 + (py - ly) ** 2 + (pz - lz) ** 2)
      const rotDist = Math.sqrt((rx - lrx) ** 2 + (ry - lry) ** 2 + (rz - lrz) ** 2)
      if (posDist + rotDist < MOVE_THRESHOLD) return
    }

    lastPosRef.current = [px, py, pz, rx, ry, rz]
    lastDispatchTimeRef.current = now
    dispatch(
      setCameraPosition({
        position: [px, py, pz],
        rotation: [rx, ry, rz],
      }),
    )
  })

  // On scene switch: save current camera → old scene, restore from new scene
  useEffect(() => {
    if (prevIndexRef.current !== activeIndex) {
      // Save current Three.js camera position + rotation to the OLD scene
      dispatch(
        setCameraPositionForScene({
          sceneIndex: prevIndexRef.current,
          position: [camera.position.x, camera.position.y, camera.position.z],
          rotation: [camera.rotation.x, camera.rotation.y, camera.rotation.z],
        }),
      )
      prevIndexRef.current = activeIndex

      // Restore camera position + rotation from the NEW scene
      const newPos = allScenes[activeIndex].cameraPosition
      const newRot = allScenes[activeIndex].cameraRotation
      camera.position.set(newPos[0], newPos[1], newPos[2])
      // Compute OrbitControls target from rotation: forward * distance
      if (controlsRef.current) {
        const forward = new THREE.Vector3(0, 0, -1).applyEuler(new THREE.Euler(newRot[0], newRot[1], newRot[2]))
        const dist = camera.position.length()
        const target = camera.position.clone().add(forward.multiplyScalar(dist))
        controlsRef.current.target.copy(target)
        // Kill damping inertia so momentum from the old scene doesn't carry over
        const wasDamping = controlsRef.current.enableDamping
        controlsRef.current.enableDamping = false
        controlsRef.current.update()
        controlsRef.current.enableDamping = wasDamping
      }
    }
  }, [activeIndex, allScenes, camera, controlsRef, dispatch])

  // Apply camera preset when triggered from sidebar
  const presetState = useSyncExternalStore(subscribeCameraPreset, getCameraPresetSnapshot)
  // Start at 0 so triggers that fired before CameraSync mounted are still applied
  const lastPresetVersion = useRef(0)

  useEffect(() => {
    if (presetState.version !== lastPresetVersion.current) {
      lastPresetVersion.current = presetState.version
      const [px, py, pz] = presetState.position
      const [rx, ry, rz] = presetState.rotation
      camera.position.set(px, py, pz)
      if (controlsRef.current) {
        // Reconstruct orbit target from rotation: project camera forward ray
        const forward = new THREE.Vector3(0, 0, -1).applyEuler(new THREE.Euler(rx, ry, rz))
        const toOrigin = new THREE.Vector3(-px, -py, -pz)
        const d = toOrigin.dot(forward)
        const target = new THREE.Vector3(px, py, pz).add(forward.multiplyScalar(d))
        controlsRef.current.target.copy(target)
        controlsRef.current.update()
      }
    }
  }, [presetState, camera, controlsRef])

  return null
}

/*
// ── PanLimiter — clamps pan so the device center stays visible ───

const PanLimiter = ({controlsRef}: {controlsRef: RefObject<OrbitControlsImpl | null>}) => {
  const {camera, size} = useThree()
  const limitsRef = useRef<{x: number; y: number} | null>(null)

  useFrame(() => {
    const controls = controlsRef.current
    if (!controls) return

    // Compute limits once from the initial camera position
    if (!limitsRef.current) {
      const d = camera.position.length() // distance from origin (device center)
      if (camera instanceof THREE.PerspectiveCamera) {
        const halfH = d * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2))
        const halfW = halfH * (size.width / size.height)
        limitsRef.current = {x: halfW * PAN_FRUSTUM_FRACTION, y: halfH * PAN_FRUSTUM_FRACTION}
      } else if (camera instanceof THREE.OrthographicCamera) {
        limitsRef.current = {
          x: ((camera.right - camera.left) / 2) * PAN_FRUSTUM_FRACTION,
          y: ((camera.top - camera.bottom) / 2) * PAN_FRUSTUM_FRACTION,
        }
      } else {
        return
      }
    }

    const {x: maxX, y: maxY} = limitsRef.current
    const t = controls.target
    const cx = THREE.MathUtils.clamp(t.x, -maxX, maxX)
    const cy = THREE.MathUtils.clamp(t.y, -maxY, maxY)
    const dx = cx - t.x
    const dy = cy - t.y

    if (dx !== 0 || dy !== 0) {
      t.x = cx
      t.y = cy
      camera.position.x += dx
      camera.position.y += dy
    }
  })

  return null
}
*/

// ── ViewportV2 — 3D viewport ────────────────────────────────────

interface ViewportV2Props {
  loading?: boolean
  onLoadingChange?: (loading: boolean) => void
  onWorkerBusyChange?: (busy: boolean) => void
  onExportReady?: (renderExport: RenderExportFn) => void
  /**
   * R3F render loop. Defaults to `'always'` (the editor). The render micro-
   * frontend passes `'never'` during the CSG build so SwiftShader doesn't burn
   * cores rendering frames while the geometry worker needs the CPU, then flips
   * to `'always'` once geometry is ready (before the BE settles + screenshots).
   */
  frameloop?: 'always' | 'never'
}

const ViewportV2 = ({
  loading,
  onLoadingChange,
  onWorkerBusyChange,
  onExportReady,
  frameloop = 'always',
}: ViewportV2Props) => {
  const allScenes = useAppSelector(selectAllScenes)
  const activeIndex = useAppSelector(selectActiveSceneIndex)
  const initialCameraPos = allScenes[0].cameraPosition
  const controlsRef = useRef<OrbitControlsImpl>(null)

  // Hold the renderer + canvas-side listeners so we can forcefully release the
  // WebGL context on unmount. R3F disposes the renderer but never calls
  // forceContextLoss(), so the underlying context can linger in the GPU process
  // long enough to overlap with the next page's Canvas mount — at which point
  // browsers may evict an active context with a "context lost" event. Mounting
  // /editor right after the landing page's embedded canvas is the canonical
  // repro.
  const glRef = useRef<THREE.WebGLRenderer | null>(null)
  const lostListenerRef = useRef<EventListener | null>(null)
  const restoredListenerRef = useRef<EventListener | null>(null)

  const handleCanvasCreated = useCallback((state: {gl: THREE.WebGLRenderer}) => {
    glRef.current = state.gl
    const canvas = state.gl.domElement
    const handleLost = (e: Event) => {
      e.preventDefault()
      addBreadcrumb('webgl', 'context-lost', 'error')
      reportError(new Error('WebGL context lost'), 'webgl.contextLost')
      window.dispatchEvent(new CustomEvent('app:webgl-lost'))
    }
    const handleRestored = () => {
      addBreadcrumb('webgl', 'context-restored', 'info')
      window.dispatchEvent(new CustomEvent('app:webgl-restored'))
    }
    lostListenerRef.current = handleLost
    restoredListenerRef.current = handleRestored
    canvas.addEventListener('webglcontextlost', handleLost, false)
    canvas.addEventListener('webglcontextrestored', handleRestored, false)
  }, [])

  useEffect(() => {
    return () => {
      const gl = glRef.current
      if (!gl) return
      const canvas = gl.domElement
      // Detach listeners first so the synthetic 'webglcontextlost' fired by
      // forceContextLoss() doesn't surface a user-visible error toast on a
      // routine navigation away.
      if (lostListenerRef.current) {
        canvas.removeEventListener('webglcontextlost', lostListenerRef.current, false)
      }
      if (restoredListenerRef.current) {
        canvas.removeEventListener('webglcontextrestored', restoredListenerRef.current, false)
      }
      try {
        gl.forceContextLoss()
      } catch {
        // Some browsers refuse forceContextLoss after the canvas was already
        // detached — ignore, R3F's own dispose() still runs after this.
      }
      glRef.current = null
      lostListenerRef.current = null
      restoredListenerRef.current = null
    }
  }, [])

  return (
    <Canvas
      shadows="variance"
      frameloop={frameloop}
      dpr={[1, 1.5]}
      onCreated={handleCanvasCreated}
      camera={{
        position: [initialCameraPos[0], initialCameraPos[1], initialCameraPos[2]],
        fov: CAMERA_FOV,
        near: CAMERA_NEAR,
        far: CAMERA_FAR,
      }}
      gl={{
        antialias: true,
        alpha: false,
        preserveDrawingBuffer: true,
        toneMapping: THREE.ACESFilmicToneMapping,
        toneMappingExposure: TONE_MAPPING_EXPOSURE,
        outputColorSpace: THREE.SRGBColorSpace,
        localClippingEnabled: true,
      }}
    >
      <SceneBackground />
      <SceneLighting />
      <SceneEnvironment />
      <NoSpecularFromLights />

      {/* Render device meshes for ALL scenes — visibility toggles on switch.
          Device-only Z-rotation: wraps `DeviceMeshV2` so the ground & 3D texts
          stay world-fixed. */}
      {allScenes.map((sceneData, idx) => (
        <group key={idx} visible={idx === activeIndex}>
          <group
            rotation={[0, 0, -(sceneData.deviceRotationZ * Math.PI) / 180]}
            position={[0, 0, sceneData.devicePositionZ]}
          >
            <DeviceMeshV2
              sceneIndex={idx}
              textureUrl={sceneData.screenImageUrl}
              onLoadingChange={idx === activeIndex ? onLoadingChange : undefined}
              onWorkerBusyChange={idx === activeIndex ? onWorkerBusyChange : undefined}
            />
            {/* Custom decals ride the device transform — rendered inside the device group.
                Hidden while the active device is still building so they don't float
                in empty space behind the loading overlay before the device appears. */}
            {!(idx === activeIndex && loading) &&
              sceneData.customDecals.map((d) => (
                <CustomDecalMeshV2
                  key={d.id}
                  decal={d}
                  deviceWidth={sceneData.deviceWidth}
                  deviceHeight={sceneData.deviceHeight}
                  deviceDepth={sceneData.deviceDepth}
                />
              ))}
          </group>
          <ShadowCatcher sceneIndex={idx} />
          {sceneData.texts3d.map((t) => (
            <Text3DMeshV2 key={t.id} text3d={t} />
          ))}
          {sceneData.sprites3d.map((s) => (
            <Sprite3DMeshV2 key={s.id} sprite={s} />
          ))}
        </group>
      ))}

      <OrbitControls
        ref={controlsRef}
        target={[0, 0, 0]}
        enabled={!loading}
        minDistance={FREE_CAMERA ? undefined : ORBIT_MIN_DISTANCE}
        maxDistance={FREE_CAMERA ? undefined : ORBIT_MAX_DISTANCE}
        minAzimuthAngle={-Math.PI / 4}
        maxAzimuthAngle={Math.PI / 4}
        minPolarAngle={Math.PI / 2 - Math.PI / 3}
        maxPolarAngle={Math.PI / 2 + Math.PI / 3}
      />
      <CameraSync controlsRef={controlsRef} />
      <ExportAspectRender />
      {onExportReady && <ExportHandler onReady={onExportReady} />}
    </Canvas>
  )
}

export default ViewportV2
